import React, { useState, useRef, useEffect } from 'react';
import './ElementProperties.css';
import IconSelector from './IconSelector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as solidIcons from '@fortawesome/free-solid-svg-icons';

function ElementProperties({ element, updateElement }) {
  const [isFontDropdownOpen, setIsFontDropdownOpen] = useState(false);
  const fontDropdownRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [shadowX, setShadowX] = useState(0);
  const [shadowY, setShadowY] = useState(0);
  const [shadowBlur, setShadowBlur] = useState(0);
  const [shadowColor, setShadowColor] = useState('#000000');
  const [iconSearchTerm, setIconSearchTerm] = useState('');
  const [selectedIconCategory, setSelectedIconCategory] = useState('All');
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ x: 0, y: 0 });
  const colorPickerRef = useRef(null);
  const eyedropperSupported = 'EyeDropper' in window;

  useEffect(() => {
    function handleClickOutside(event) {
      if (fontDropdownRef.current && !fontDropdownRef.current.contains(event.target)) {
        setIsFontDropdownOpen(false);
      }
    }
    
    if (isFontDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFontDropdownOpen]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
        setIsColorPickerOpen(false);
      }
    }
    
    if (isColorPickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isColorPickerOpen]);

  useEffect(() => {
    if (element && element.type === 'shape') {
      const boxShadow = element.style?.boxShadow || 'none';
      
      if (boxShadow !== 'none') {
        const shadowParts = boxShadow.match(/([0-9]+)px\s([0-9]+)px\s([0-9]+)px\s(rgba?\(.*?\)|#[0-9a-fA-F]{3,8})/);
        if (shadowParts) {
          setShadowX(parseInt(shadowParts[1]));
          setShadowY(parseInt(shadowParts[2]));
          setShadowBlur(parseInt(shadowParts[3]));
          setShadowColor(shadowParts[4]);
        }
      } else {
        // Reset shadow values when 'none'
        setShadowX(0);
        setShadowY(0);
        setShadowBlur(0);
        setShadowColor('#000000');
      }
    }
  }, [element, element?.style?.boxShadow]);

  const fonts = [
    { name: 'Arimo', value: 'Arimo, sans-serif' },
    { name: 'Canva Sans', value: 'Canva Sans, sans-serif' },
    { name: 'Montserrat', value: 'Montserrat, sans-serif' },
    { name: 'Open Sans', value: 'Open Sans, sans-serif' },
    { name: 'Poppins', value: 'Poppins, sans-serif' },
    { name: 'Glacial Indifference', value: 'Glacial Indifference, sans-serif' },
    { name: 'League Spartan', value: 'League Spartan, sans-serif' },
    { name: 'Anton', value: 'Anton, sans-serif' },
    { name: 'DM Sans', value: 'DM Sans, sans-serif' },
    { name: 'Archivo Black', value: 'Archivo Black, sans-serif' },
    { name: 'Roboto', value: 'Roboto, sans-serif' },
    { name: 'Garet', value: 'Garet, sans-serif' },
    { name: 'Alice', value: 'Alice, serif' },
    { name: 'Open Sauce', value: 'Open Sauce, sans-serif' },
    { name: 'Brittany', value: 'Brittany, cursive' },
    { name: 'Arial', value: 'Arial, sans-serif' }
  ];

  // Return early if no element is selected
  if (!element) return null;
  
  const handleContentChange = (e) => {
    updateElement(element.id, { content: e.target.value });
  };
  
  const handleStyleChange = (property, value) => {
    updateElement(element.id, { 
      style: { ...element.style, [property]: value } 
    });
  };

  const handleResetStyle = (property) => {
    const defaultValues = {
      fontSize: '16px',
      color: '#000000',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textAlign: 'left',
      letterSpacing: '0px',
      lineHeight: '1.5'
    };
    
    handleStyleChange(property, defaultValues[property]);
  };
  
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    console.log("🖼️ Image file selected in ElementProperties:", {
      name: file.name,
      size: `${(file.size / 1024).toFixed(2)} KB`,
      type: file.type
    });
    
    try {
      // Import the uploadFile function and useAuth hook dynamically to avoid circular dependencies
      const { uploadFile } = await import('../services/storage');
      const { auth } = await import('../services/firebase');
      
      // Check if user is authenticated
      if (!auth.currentUser) {
        console.error("❌ No user is logged in");
        alert("You must be logged in to upload images");
        return;
      }
      
      // Show loading indicator
      setIsUploading(true);
      setUploadProgress(0);
      updateElement(element.id, { 
        isLoading: true 
      });
      
      // Upload to Firebase Storage
      console.log("⬆️ Uploading to Firebase Storage...");
      const { url, path, fileName } = await uploadFile(
        file, 
        auth.currentUser.uid, 
        null, // No invitation ID in this context
        (progress) => {
          console.log(`Upload progress: ${progress.toFixed(2)}%`);
          setUploadProgress(progress);
        }
      );
      
      console.log("✅ Firebase upload complete:", url);
      
      // Update the element with the Firebase Storage URL
      updateElement(element.id, { 
        content: url, // Use the Firebase URL instead of base64
        storagePath: path,
        isLoading: false
      });
      
      setIsUploading(false);
    } catch (error) {
      console.error("❌ Upload failed:", error);
      alert(`Upload failed: ${error.message}`);
      // Reset loading state
      updateElement(element.id, { isLoading: false });
      setIsUploading(false);
    }
  };
  
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    console.log("🎬 Video file selected:", {
      name: file.name,
      size: `${(file.size / 1024).toFixed(2)} KB`,
      type: file.type
    });
    
    // Check file size limit (50MB)
    if (file.size > 50000000) {
      alert('Video file is too large. Maximum size is 50MB.');
      return;
    }
    
    try {
      // Import the uploadFile function and auth dynamically to avoid circular dependencies
      const { uploadFile } = await import('../services/storage');
      const { auth } = await import('../services/firebase');
      
      // Check if user is authenticated
      if (!auth.currentUser) {
        console.error("❌ No user is logged in");
        alert("You must be logged in to upload videos");
        return;
      }
      
      // Show loading indicator
      setIsUploading(true);
      setUploadProgress(0);
      updateElement(element.id, { 
        isLoading: true 
      });
      
      // Upload to Firebase Storage
      console.log("⬆️ Uploading video to Firebase Storage...");
      const { url, path, fileName } = await uploadFile(
        file, 
        auth.currentUser.uid, 
        null, // No invitation ID in this context
        (progress) => {
          console.log(`Upload progress: ${progress.toFixed(2)}%`);
          setUploadProgress(progress);
        }
      );
      
      console.log("✅ Firebase video upload complete:", url);
      
      // Update the element with the Firebase Storage URL
      updateElement(element.id, { 
        content: url, // Use the Firebase URL instead of blob URL
        storagePath: path,
        isLoading: false
      });
      
      setIsUploading(false);
    } catch (error) {
      console.error("❌ Video upload failed:", error);
      alert(`Upload failed: ${error.message}`);
      // Reset loading state
      updateElement(element.id, { isLoading: false });
      setIsUploading(false);
    }
  };
  
  const renderShapeProperties = () => {
    // Only render shape properties for shape elements
    if (element.type !== 'shape') return null;
    
    const shapeType = element.shapeType || 'square';
    // Use both fill and backgroundColor to determine the current color
    const fillColor = element.style?.fill || element.style?.backgroundColor || '#4f46e5'; 
    const opacity = element.style?.opacity !== undefined ? element.style.opacity : 1;
    const boxShadow = element.style?.boxShadow || 'none';
    
    // Define a dedicated color change handler for shapes
    const handleShapeColorChange = (color) => {
      console.log('Updating shape color to:', color);
      // Update both fill and backgroundColor properties to ensure compatibility
      updateElement(element.id, {
        style: {
          ...element.style,
          fill: color,
          backgroundColor: color
        }
      });
    };

    // New function for eyedropper tool
    const activateEyedropper = async () => {
      try {
        // Check if browser supports the EyeDropper API
        if (eyedropperSupported) {
          const eyeDropper = new window.EyeDropper();
          const result = await eyeDropper.open();
          handleShapeColorChange(result.sRGBHex);
        } else {
          alert('Your browser does not support the eyedropper tool. Please use Chrome or Edge.');
        }
      } catch (error) {
        console.error('Eyedropper error:', error);
      }
    };
    
    const updateShadow = (offsetX, offsetY, blur, color) => {
      const shadowValue = offsetX === 0 && offsetY === 0 && blur === 0 
        ? 'none' 
        : `${offsetX}px ${offsetY}px ${blur}px ${color}`;
      
      // Use direct element update for shape shadows
      if (element.type === 'shape') {
        updateElement(element.id, {
          style: {
            ...element.style,
            boxShadow: shadowValue
          }
        });
      } else {
        // Use handleStyleChange for other elements
        handleStyleChange('boxShadow', shadowValue);
      }
    };

    return (
      <div className="shape-properties">
        <h3>Shape Properties</h3>
        
        <div className="property-row">
          <label>Shape Type</label>
          <div className="shape-type-selector">
            <button 
              className={`shape-btn ${shapeType === 'square' ? 'active' : ''}`}
              onClick={() => updateElement(element.id, { shapeType: 'square' })}
              title="Square"
            >
              <div className="shape-preview">
                <div className="shape-square"></div>
              </div>
            </button>
            <button 
              className={`shape-btn ${shapeType === 'circle' ? 'active' : ''}`}
              onClick={() => updateElement(element.id, { shapeType: 'circle' })}
              title="Circle"
            >
              <div className="shape-preview">
                <div className="shape-circle"></div>
              </div>
            </button>
            <button 
              className={`shape-btn ${shapeType === 'triangle' ? 'active' : ''}`}
              onClick={() => updateElement(element.id, { shapeType: 'triangle' })}
              title="Triangle"
            >
              <div className="shape-preview">
                <div className="shape-triangle"></div>
              </div>
            </button>
            <button 
              className={`shape-btn ${shapeType === 'line' ? 'active' : ''}`}
              onClick={() => updateElement(element.id, { shapeType: 'line' })}
              title="Line"
            >
              <div className="shape-preview">
                <div className="shape-line"></div>
              </div>
            </button>
            <button 
              className={`shape-btn ${shapeType === 'arrow' ? 'active' : ''}`}
              onClick={() => updateElement(element.id, { shapeType: 'arrow' })}
              title="Arrow"
            >
              <div className="shape-preview">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4L10.59 5.41L16.17 11H4V13H16.17L10.59 18.59L12 20L20 12L12 4Z" fill="#5c5cff" />
                </svg>
              </div>
            </button>
            <button 
              className={`shape-btn ${shapeType === 'star' ? 'active' : ''}`}
              onClick={() => updateElement(element.id, { shapeType: 'star' })}
              title="Star"
            >
              <div className="shape-preview">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" fill="#5c5cff" />
                </svg>
              </div>
            </button>
          </div>
        </div>
        
        <div className="property-row">
          <label>Fill Color</label>
          <div className="color-input-box">
            <div className="color-square" style={{ backgroundColor: fillColor }}></div>
            <div className="hex-input-container">
              <span className="hash-symbol">#</span>
              <input 
                type="text" 
                value={fillColor.replace('#', '')}
                onChange={(e) => handleShapeColorChange(`#${e.target.value}`)}
                className="hex-input"
              />
            </div>
            <div className="color-picker-wrapper">
              <div className="color-picker-icon">
                <input 
                  type="color" 
                  value={fillColor} 
                  onChange={(e) => handleShapeColorChange(e.target.value)}
                  className="hidden-color-input"
                />
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" fill={fillColor} stroke="#ddd" strokeWidth="1" />
                </svg>
              </div>
              {eyedropperSupported && (
                <button 
                  className="eyedropper-button" 
                  onClick={activateEyedropper}
                  title="Pick color from screen"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.5 5.5L19 7L7 19L5 17.5L17.5 5.5Z" fill="currentColor"/>
                    <path d="M18 3C17.4 3 16.8 3.2 16.3 3.7L14.6 5.4L18.6 9.4L20.3 7.7C21.3 6.7 21.3 5.2 20.3 4.2L19.8 3.7C19.3 3.2 18.7 3 18 3ZM14.6 5.4L3 17V21H7L18.6 9.4L14.6 5.4Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
          
          {isColorPickerOpen && (
            <div 
              className="color-picker-popover"
              ref={colorPickerRef}
              style={{
                top: pickerPosition.y + 'px',
                left: pickerPosition.x + 'px'
              }}
            >
              <div className="color-picker-header">
                <span>Color Picker</span>
                <button onClick={() => setIsColorPickerOpen(false)}>×</button>
              </div>
              <div className="gradient-picker">
                <div className="color-gradient"></div>
              </div>
              <div className="color-sliders">
                <div className="color-slider">
                  <label>R</label>
                  <input type="range" min="0" max="255" />
                  <input type="number" min="0" max="255" />
                </div>
                <div className="color-slider">
                  <label>G</label>
                  <input type="range" min="0" max="255" />
                  <input type="number" min="0" max="255" />
                </div>
                <div className="color-slider">
                  <label>B</label>
                  <input type="range" min="0" max="255" />
                  <input type="number" min="0" max="255" />
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="property-row property-slider">
          <label>Opacity</label>
          <div className="modern-slider-container">
            <input 
              type="range" 
              min="0" 
              max="100" 
              value={Math.round(opacity * 100)} 
              onChange={(e) => {
                const value = parseInt(e.target.value);
                e.target.style.setProperty('--value-percent', `${value}%`);
                // Use direct element update instead of handleStyleChange
                updateElement(element.id, {
                  style: {
                    ...element.style,
                    opacity: value / 100
                  }
                });
              }}
              className="modern-slider"
              style={{ '--value-percent': `${Math.round(opacity * 100)}%` }}
            />
            <div className="slider-value">{Math.round(opacity * 100)}%</div>
          </div>
        </div>
        
        <div className="property-header">
          <h3>SHADOW</h3>
        </div>
        
        <div className="shadow-controls">
          <div className="shadow-row">
            <label>X Offset</label>
            <input
              type="number"
              value={shadowX}
              onChange={(e) => {
                const newValue = parseInt(e.target.value, 10);
                setShadowX(newValue);
                updateShadow(newValue, shadowY, shadowBlur, shadowColor);
              }}
            />
          </div>
          <div className="shadow-row">
            <label>Y Offset</label>
            <input
              type="number"
              value={shadowY}
              onChange={(e) => {
                const newValue = parseInt(e.target.value, 10);
                setShadowY(newValue);
                updateShadow(shadowX, newValue, shadowBlur, shadowColor);
              }}
            />
          </div>
          <div className="shadow-row">
            <label>Blur</label>
            <input
              type="number"
              value={shadowBlur}
              onChange={(e) => {
                const newValue = parseInt(e.target.value, 10);
                setShadowBlur(newValue);
                updateShadow(shadowX, shadowY, newValue, shadowColor);
              }}
            />
          </div>
          <div className="shadow-row">
            <label>Color</label>
            <input
              type="color"
              value={shadowColor}
              onChange={(e) => {
                setShadowColor(e.target.value);
                updateShadow(shadowX, shadowY, shadowBlur, e.target.value);
              }}
            />
          </div>
          <div className="shadow-row">
            <button 
              className="reset-shadow-btn"
              onClick={() => {
                if (element.type === 'shape') {
                  updateElement(element.id, {
                    style: {
                      ...element.style,
                      boxShadow: 'none'
                    }
                  });
                } else {
                  handleStyleChange('boxShadow', 'none');
                }
              }}
            >
              Reset Shadow
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // Add a new function to render RSVP button properties
  const renderRsvpButtonProperties = () => {
    if (element.type !== 'rsvp-button') return null;
    
    // Extract current styles with defaults
    const {
      backgroundColor = '#4caf50',
      textColor = '#ffffff',
      text = 'RSVP Now',
      borderRadius = '4px',
      fontSize = '16px',
      fontWeight = 'bold',
      boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)',
      buttonStyle = 'filled' // filled, outlined, text
    } = element.style || {};
    
    return (
      <>
        <div className="property-header">
          <h3>BUTTON TEXT</h3>
          <button className="reset-btn" onClick={() => handleStyleChange('text', 'RSVP Now')}>Reset</button>
        </div>
        
        <div className="property-group">
          <input 
            type="text" 
            value={text} 
            onChange={(e) => handleStyleChange('text', e.target.value)}
            placeholder="Button text"
            className="full-width-input"
          />
        </div>
        
        <div className="property-header">
          <h3>BUTTON STYLE</h3>
        </div>
        
        <div className="property-group">
          <div className="button-style-selector">
            <button 
              className={`style-btn ${buttonStyle === 'filled' ? 'active' : ''}`}
              onClick={() => handleStyleChange('buttonStyle', 'filled')}
              title="Filled Button"
            >
              <div className="style-preview filled-preview"></div>
              <span>Filled</span>
            </button>
            <button 
              className={`style-btn ${buttonStyle === 'outlined' ? 'active' : ''}`}
              onClick={() => handleStyleChange('buttonStyle', 'outlined')}
              title="Outlined Button"
            >
              <div className="style-preview outlined-preview"></div>
              <span>Outlined</span>
            </button>
            <button 
              className={`style-btn ${buttonStyle === 'text' ? 'active' : ''}`}
              onClick={() => handleStyleChange('buttonStyle', 'text')}
              title="Text Button"
            >
              <div className="style-preview text-preview"></div>
              <span>Text</span>
            </button>
          </div>
        </div>
        
        <div className="property-header">
          <h3>COLORS</h3>
        </div>
        
        <div className="property-row">
          <label>Background</label>
          <div className="color-input-box">
            <div className="color-square" style={{ backgroundColor }}></div>
            <div className="hex-input-container">
              <span className="hash-symbol">#</span>
              <input 
                type="text" 
                value={backgroundColor.replace('#', '')}
                onChange={(e) => handleStyleChange('backgroundColor', `#${e.target.value}`)}
                className="hex-input"
              />
            </div>
            <div className="color-picker-icon">
              <input 
                type="color" 
                value={backgroundColor} 
                onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
                className="hidden-color-input"
              />
              <svg width="24" height="24" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" fill={backgroundColor} stroke="#ddd" strokeWidth="1" />
              </svg>
            </div>
          </div>
        </div>
        
        <div className="property-row">
          <label>Text Color</label>
          <div className="color-input-box">
            <div className="color-square" style={{ backgroundColor: textColor }}></div>
            <div className="hex-input-container">
              <span className="hash-symbol">#</span>
              <input 
                type="text" 
                value={textColor.replace('#', '')}
                onChange={(e) => handleStyleChange('textColor', `#${e.target.value}`)}
                className="hex-input"
              />
            </div>
            <div className="color-picker-icon">
              <input 
                type="color" 
                value={textColor} 
                onChange={(e) => handleStyleChange('textColor', e.target.value)}
                className="hidden-color-input"
              />
              <svg width="24" height="24" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" fill={textColor} stroke="#ddd" strokeWidth="1" />
              </svg>
            </div>
          </div>
        </div>
        
        <div className="property-header">
          <h3>APPEARANCE</h3>
        </div>
        
        <div className="property-row">
          <label>Font Size</label>
          <div className="input-with-buttons">
            <button 
              className="decrement-btn"
              onClick={() => {
                const currentSize = parseInt(fontSize);
                const newSize = Math.max(8, currentSize - 1);
                handleStyleChange('fontSize', `${newSize}px`);
              }}
            >-</button>
            <input 
              type="text" 
              value={(fontSize || '16px').replace('px', '')}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d+$/.test(value)) {
                  handleStyleChange('fontSize', `${value}px`);
                }
              }}
              onBlur={(e) => {
                const value = e.target.value;
                const size = parseInt(value) || 16;
                const validSize = Math.max(8, size);
                handleStyleChange('fontSize', `${validSize}px`);
              }}
              className="number-input"
            />
            <button 
              className="increment-btn"
              onClick={() => {
                const currentSize = parseInt(fontSize) || 16;
                const newSize = currentSize + 1;
                handleStyleChange('fontSize', `${newSize}px`);
              }}
            >+</button>
            <span className="unit">px</span>
          </div>
        </div>
        
        <div className="property-row">
          <label>Border Radius</label>
          <div className="input-with-buttons">
            <button 
              className="decrement-btn"
              onClick={() => {
                const currentRadius = parseInt(borderRadius);
                if (currentRadius > 0) {
                  handleStyleChange('borderRadius', `${currentRadius - 1}px`);
                }
              }}
            >-</button>
            <input 
              type="text" 
              value={borderRadius.replace('px', '')}
              onChange={(e) => handleStyleChange('borderRadius', `${e.target.value}px`)}
              className="number-input"
            />
            <button 
              className="increment-btn"
              onClick={() => {
                const currentRadius = parseInt(borderRadius);
                handleStyleChange('borderRadius', `${currentRadius + 1}px`);
              }}
            >+</button>
            <span className="unit">px</span>
          </div>
        </div>
        
        <div className="property-row">
          <label>Font Weight</label>
          <select 
            value={fontWeight}
            onChange={(e) => handleStyleChange('fontWeight', e.target.value)}
            className="select-input"
          >
            <option value="normal">Normal</option>
            <option value="bold">Bold</option>
            <option value="lighter">Light</option>
          </select>
        </div>
      </>
    );
  };
  
  // Add RSVP Form styles
  const renderRsvpFormProperties = () => {
    if (element.type !== 'rsvp-form') return null;
    
    // Extract current styles with defaults
    const {
      backgroundColor = '#ffffff',
      borderColor = '#e5e7eb',
      borderRadius = '8px',
      boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)'
    } = element.style || {};
    
    return (
      <>
        <div className="property-header">
          <h3>FORM APPEARANCE</h3>
        </div>
        
        <div className="property-row">
          <label>Background</label>
          <div className="color-input-box">
            <div className="color-square" style={{ backgroundColor }}></div>
            <div className="hex-input-container">
              <span className="hash-symbol">#</span>
              <input 
                type="text" 
                value={backgroundColor.replace('#', '')}
                onChange={(e) => handleStyleChange('backgroundColor', `#${e.target.value}`)}
                className="hex-input"
              />
            </div>
            <div className="color-picker-icon">
              <input 
                type="color" 
                value={backgroundColor} 
                onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
                className="hidden-color-input"
              />
              <svg width="24" height="24" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" fill={backgroundColor} stroke="#ddd" strokeWidth="1" />
              </svg>
            </div>
          </div>
        </div>
        
        <div className="property-row">
          <label>Border Color</label>
          <div className="color-input-box">
            <div className="color-square" style={{ backgroundColor: borderColor }}></div>
            <div className="hex-input-container">
              <span className="hash-symbol">#</span>
              <input 
                type="text" 
                value={borderColor.replace('#', '')}
                onChange={(e) => handleStyleChange('borderColor', `#${e.target.value}`)}
                className="hex-input"
              />
            </div>
            <div className="color-picker-icon">
              <input 
                type="color" 
                value={borderColor} 
                onChange={(e) => handleStyleChange('borderColor', e.target.value)}
                className="hidden-color-input"
              />
              <svg width="24" height="24" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" fill={borderColor} stroke="#ddd" strokeWidth="1" />
              </svg>
            </div>
          </div>
        </div>
        
        <div className="property-row">
          <label>Border Radius</label>
          <div className="input-with-buttons">
            <button 
              className="decrement-btn"
              onClick={() => {
                const currentRadius = parseInt(borderRadius);
                if (currentRadius > 0) {
                  handleStyleChange('borderRadius', `${currentRadius - 1}px`);
                }
              }}
            >-</button>
            <input 
              type="text" 
              value={borderRadius.replace('px', '')}
              onChange={(e) => handleStyleChange('borderRadius', `${e.target.value}px`)}
              className="number-input"
            />
            <button 
              className="increment-btn"
              onClick={() => {
                const currentRadius = parseInt(borderRadius);
                handleStyleChange('borderRadius', `${currentRadius + 1}px`);
              }}
            >+</button>
            <span className="unit">px</span>
          </div>
        </div>
        
        <div className="property-row">
          <label>Shadow</label>
          <select 
            value={boxShadow}
            onChange={(e) => handleStyleChange('boxShadow', e.target.value)}
            className="select-input"
          >
            <option value="none">None</option>
            <option value="0 2px 4px rgba(0, 0, 0, 0.05)">Light</option>
            <option value="0 4px 6px rgba(0, 0, 0, 0.05)">Medium</option>
            <option value="0 8px 16px rgba(0, 0, 0, 0.1)">Heavy</option>
          </select>
        </div>
      </>
    );
  };

  // Add Background element properties
  const renderBackgroundProperties = () => {
    if (element.type !== 'background') return null;
    
    // Get current background properties with defaults
    const backgroundType = element.backgroundType || 'color';
    const backgroundColor = element.style?.backgroundColor || '#ffffff';
    const backgroundImage = element.style?.backgroundImage || '';
    
    return (
      <>
        <div className="property-header">
          <h3>BACKGROUND</h3>
        </div>
        
        <div className="property-row">
          <label>Type</label>
          <div className="button-toggle">
            <button 
              className={`toggle-btn ${backgroundType === 'color' ? 'active' : ''}`}
              onClick={() => {
                updateElement(element.id, { 
                  backgroundType: 'color',
                  style: {
                    ...element.style,
                    backgroundImage: ''
                  }
                });
              }}
            >
              Color
            </button>
            <button 
              className={`toggle-btn ${backgroundType === 'image' ? 'active' : ''}`}
              onClick={() => {
                updateElement(element.id, { 
                  backgroundType: 'image'
                });
              }}
            >
              Image
            </button>
          </div>
        </div>
        
        {backgroundType === 'color' ? (
          <div className="property-row">
            <label>Background Color</label>
            <div className="color-input-box">
              <div className="color-square" style={{ backgroundColor }}></div>
              <div className="hex-input-container">
                <span className="hash-symbol">#</span>
                <input 
                  type="text" 
                  value={backgroundColor.replace('#', '')}
                  onChange={(e) => handleStyleChange('backgroundColor', `#${e.target.value}`)}
                  className="hex-input"
                />
              </div>
              <div className="color-picker-icon">
                <input 
                  type="color" 
                  value={backgroundColor} 
                  onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
                  className="hidden-color-input"
                />
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" fill={backgroundColor} stroke="#ddd" strokeWidth="1" />
                </svg>
              </div>
            </div>
          </div>
        ) : (
          <div className="property-row">
            <label>Background Image</label>
            <div className="upload-container">
              <label htmlFor="bg-image-upload" className="upload-btn">Upload Image</label>
              <input 
                id="bg-image-upload" 
                type="file" 
                accept="image/*" 
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  
                  // Show loading indicator
                  setIsUploading(true);
                  updateElement(element.id, { isLoading: true });
                  
                  // Create a FileReader to read the image
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    // Update the element with the image data
                    updateElement(element.id, {
                      style: {
                        ...element.style,
                        backgroundImage: `url(${event.target.result})`
                      },
                      isLoading: false
                    });
                    setIsUploading(false);
                  };
                  reader.readAsDataURL(file);
                }}
                style={{ display: 'none' }} 
              />
            </div>
            
            {/* Show preview of uploaded image */}
            {backgroundImage && (
              <div className="property-row image-preview-container">
                <img 
                  src={backgroundImage.replace('url(', '').replace(')', '')} 
                  alt="Background preview" 
                  className="background-image-preview" 
                />
                <button 
                  className="remove-image-btn"
                  onClick={() => {
                    updateElement(element.id, {
                      style: {
                        ...element.style,
                        backgroundImage: ''
                      }
                    });
                  }}
                >
                  Remove Image
                </button>
              </div>
            )}
            
            {isUploading && (
              <div className="upload-progress-container">
                <div className="spinner"></div>
                <div className="progress-text">Uploading image...</div>
              </div>
            )}
          </div>
        )}
        
        <div className="property-row property-info">
          <p>This background will be applied to the entire canvas area.</p>
          <p>The background will persist across all modes - edit, preview, and published invitations.</p>
        </div>
      </>
    );
  };

  const renderIconProperties = () => {
    if (element.type !== 'icon') return null;

    const { style = {} } = element;
    const color = style.color || '#333333';
    const size = parseInt(style.width) || 48;
    const opacity = parseFloat(style.opacity) || 1;
    const rotation = parseInt(style.transform?.replace('rotate(', '')?.replace('deg)', '')) || 0;

    const categories = {
      'All': Object.keys(solidIcons),
      'Wedding': ['heart', 'ring', 'gift', 'glass-cheers', 'dove', 'church'],
      'Travel': ['plane', 'car', 'train', 'ship', 'compass', 'map-marker'],
      'Food & Drinks': ['utensils', 'wine-glass', 'coffee', 'pizza-slice', 'hamburger'],
      'Nature': ['tree', 'leaf', 'sun', 'moon', 'mountain', 'cloud']
    };

    const getFilteredIcons = () => {
      const categoryIcons = categories[selectedIconCategory];
      return categoryIcons.filter(iconName => 
        iconName.toLowerCase().includes(iconSearchTerm.toLowerCase())
      );
    };

    return (
      <>
        <div className="property-header">
          <h3>SELECT ICON</h3>
        </div>

        <div className="property-group">
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search icons..."
              value={iconSearchTerm}
              onChange={(e) => setIconSearchTerm(e.target.value)}
            />
          </div>

          <div className="category-tabs">
            {Object.keys(categories).map(category => (
              <button
                key={category}
                className={`category-tab ${selectedIconCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedIconCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="icons-grid">
            {getFilteredIcons().map(iconName => {
              const IconComponent = solidIcons[`fa${iconName.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('')}`];
              if (!IconComponent) return null;

              return (
                <div
                  key={iconName}
                  className="icon-item"
                  onClick={() => updateElement(element.id, { icon: IconComponent })}
                  title={iconName}
                >
                  <FontAwesomeIcon icon={IconComponent} />
                </div>
              );
            })}
          </div>
        </div>

        <div className="property-header">
          <h3>ICON SETTINGS</h3>
          <button className="reset-btn" onClick={() => {
            handleStyleChange('color', '#333333');
            handleStyleChange('width', '48px');
            handleStyleChange('height', '48px');
            handleStyleChange('opacity', 1);
            handleStyleChange('transform', 'rotate(0deg)');
          }}>Reset</button>
        </div>

        <div className="property-group">
          <div className="property-row">
            <label>Color</label>
            <div className="color-input-box">
              <div className="color-square" style={{ backgroundColor: color }}></div>
              <div className="hex-input-container">
                <span className="hash-symbol">#</span>
                <input 
                  type="text" 
                  value={color.replace('#', '')}
                  onChange={(e) => handleStyleChange('color', `#${e.target.value}`)}
                  className="hex-input"
                />
              </div>
              <div className="color-picker-icon">
                <input 
                  type="color" 
                  value={color}
                  onChange={(e) => handleStyleChange('color', e.target.value)}
                  className="hidden-color-input"
                />
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" fill={color} stroke="#ddd" strokeWidth="1" />
                </svg>
              </div>
            </div>
          </div>

          <div className="property-row">
            <label>Size</label>
            <div className="slider-with-value">
              <input
                type="range"
                min="16"
                max="96"
                value={size}
                onChange={(e) => {
                  const newSize = `${e.target.value}px`;
                  handleStyleChange('width', newSize);
                  handleStyleChange('height', newSize);
                }}
              />
              <span className="value-display">{size}px</span>
            </div>
          </div>

          <div className="property-row">
            <label>Opacity</label>
            <div className="slider-with-value">
              <input
                type="range"
                min="0"
                max="100"
                value={opacity * 100}
                onChange={(e) => handleStyleChange('opacity', parseInt(e.target.value) / 100)}
              />
              <span className="value-display">{Math.round(opacity * 100)}%</span>
            </div>
          </div>

          <div className="property-row">
            <label>Rotation</label>
            <div className="slider-with-value">
              <input
                type="range"
                min="0"
                max="360"
                value={rotation}
                onChange={(e) => handleStyleChange('transform', `rotate(${e.target.value}deg)`)}
              />
              <span className="value-display">{rotation}°</span>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="element-properties">
      {element.type === 'text' && (
        <>
          <div className="property-header">
            <h3>TEXT EDITOR</h3>
            <button className="reset-btn" onClick={() => handleStyleChange('content', '')}>Clear</button>
          </div>
          
          <div className="property-group">
            <textarea 
              value={element.content} 
              onChange={handleContentChange}
              rows={6}
              placeholder="Enter your text here..."
            />
          </div>
          
          <div className="property-header">
            <h3>TEXT STYLE</h3>
            <button className="reset-btn" onClick={() => handleResetStyle('fontFamily')}>Reset</button>
          </div>
          
          <div className="property-group">
            <div className="font-dropdown-container" ref={fontDropdownRef}>
              <div 
                className="font-dropdown-header" 
                onClick={() => setIsFontDropdownOpen(!isFontDropdownOpen)}
              >
                <span style={{ fontFamily: element.style.fontFamily || 'Arial, sans-serif' }}>
                  {element.style.fontFamily ? 
                    fonts.find(f => f.value === element.style.fontFamily)?.name || 'Arial' 
                    : 'Arial'}
                </span>
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg" className={isFontDropdownOpen ? 'arrow-up' : ''}>
                  <path d="M1 1L5 5L9 1" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              {isFontDropdownOpen && (
                <div className="font-dropdown-menu">
                  {fonts.map(font => (
                    <div 
                      key={font.name} 
                      className="font-item"
                      style={{ fontFamily: font.value }}
                      onClick={() => {
                        handleStyleChange('fontFamily', font.value);
                        setIsFontDropdownOpen(false);
                      }}
                    >
                      {font.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="property-header">
            <h3>TEXT SIZE</h3>
            <button className="reset-btn" onClick={() => handleResetStyle('fontSize')}>Reset</button>
          </div>
          
          <div className="property-group">
            <div className="number-input-container">
              <button
                className="number-btn decrease"
                onClick={() => {
                  const currentSize = parseInt(element.style.fontSize) || 16;
                  const newSize = Math.max(8, currentSize - 1);
                  handleStyleChange('fontSize', `${newSize}px`);
                }}
              >
                −
              </button>
              <input
                type="text"
                className="number-input"
                value={(element.style.fontSize || '16px').replace('px', '')}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d+$/.test(value)) {
                    handleStyleChange('fontSize', `${value}px`);
                  }
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  const size = parseInt(value) || 16;
                  const validSize = Math.max(8, size);
                  handleStyleChange('fontSize', `${validSize}px`);
                }}
              />
              <button
                className="number-btn increase"
                onClick={() => {
                  const currentSize = parseInt(element.style.fontSize) || 16;
                  const newSize = currentSize + 1;
                  handleStyleChange('fontSize', `${newSize}px`);
                }}
              >
                +
              </button>
            </div>
          </div>
          
          <div className="property-header">
            <h3>TEXT COLOR</h3>
            <button className="reset-btn" onClick={() => handleResetStyle('color')}>Reset</button>
          </div>
          
          <div className="property-group">
            <div className="color-search">
              <input 
                type="text" 
                placeholder="Search for a color" 
                className="color-search-input"
              />
            </div>
            
            <div className="color-input-box">
              <div className="color-square" style={{ backgroundColor: element.style.color || '#333333' }}></div>
              <div className="hex-input-container">
                <span className="hash-symbol">#</span>
                <input 
                  type="text" 
                  value={(element.style.color || '#333333').replace('#', '')}
                  onChange={(e) => handleStyleChange('color', `#${e.target.value}`)}
                  className="hex-input"
                />
              </div>
              <div className="color-picker-icon">
                <input 
                  type="color" 
                  value={element.style.color || '#333333'} 
                  onChange={(e) => handleStyleChange('color', e.target.value)}
                  className="hidden-color-input"
                />
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <linearGradient id="rainbow" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgb(255, 0, 0)" />
                    <stop offset="16.67%" stopColor="rgb(255, 255, 0)" />
                    <stop offset="33.33%" stopColor="rgb(0, 255, 0)" />
                    <stop offset="50%" stopColor="rgb(0, 255, 255)" />
                    <stop offset="66.67%" stopColor="rgb(0, 0, 255)" />
                    <stop offset="83.33%" stopColor="rgb(255, 0, 255)" />
                    <stop offset="100%" stopColor="rgb(255, 0, 0)" />
                  </linearGradient>
                  <circle cx="12" cy="12" r="10" fill="url(#rainbow)" stroke="#ddd" strokeWidth="1" />
                </svg>
              </div>
            </div>
          </div>
          
          <div className="property-header">
            <h3>TEXT FORMAT</h3>
            <button className="reset-btn" onClick={() => {
              handleResetStyle('fontWeight');
              handleResetStyle('fontStyle');
              handleResetStyle('textAlign');
            }}>Reset</button>
          </div>
          
          <div className="property-group">
            <div className="format-options">
              <div className="case-options">
                <button 
                  className={`format-btn ${element.style.textTransform === 'uppercase' ? 'active' : ''}`}
                  onClick={() => handleStyleChange('textTransform', 'uppercase')}
                >
                  AA
                </button>
                <button 
                  className={`format-btn ${element.style.textTransform === 'lowercase' ? 'active' : ''}`}
                  onClick={() => handleStyleChange('textTransform', 'lowercase')}
                >
                  aa
                </button>
                <button 
                  className={`format-btn ${element.style.textTransform === 'capitalize' ? 'active' : ''}`}
                  onClick={() => handleStyleChange('textTransform', 'capitalize')}
                >
                  Aa
                </button>
              </div>
              <div className="style-options">
                <button 
                  className={`format-btn ${element.style.fontWeight === 'bold' ? 'active' : ''}`}
                  onClick={() => handleStyleChange('fontWeight', element.style.fontWeight === 'bold' ? 'normal' : 'bold')}
                >
                  B
                </button>
                <button 
                  className={`format-btn ${element.style.fontStyle === 'italic' ? 'active' : ''}`}
                  onClick={() => handleStyleChange('fontStyle', element.style.fontStyle === 'italic' ? 'normal' : 'italic')}
                >
                  I
                </button>
                <button 
                  className={`format-btn ${element.style.textDecoration === 'underline' ? 'active' : ''}`}
                  onClick={() => handleStyleChange('textDecoration', element.style.textDecoration === 'underline' ? 'none' : 'underline')}
                >
                  U
                </button>
              </div>
            </div>
            <div className="align-options">
              <button 
                className={`align-btn ${element.style.textAlign === 'left' ? 'active' : ''}`}
                onClick={() => handleStyleChange('textAlign', 'left')}
              >
                <div className="align-icon align-left">
                  <div className="align-line"></div>
                  <div className="align-line short"></div>
                  <div className="align-line medium"></div>
                </div>
              </button>
              <button 
                className={`align-btn ${element.style.textAlign === 'center' ? 'active' : ''}`}
                onClick={() => handleStyleChange('textAlign', 'center')}
              >
                <div className="align-icon align-center">
                  <div className="align-line"></div>
                  <div className="align-line short"></div>
                  <div className="align-line medium"></div>
                </div>
              </button>
              <button 
                className={`align-btn ${element.style.textAlign === 'right' ? 'active' : ''}`}
                onClick={() => handleStyleChange('textAlign', 'right')}
              >
                <div className="align-icon align-right">
                  <div className="align-line"></div>
                  <div className="align-line short"></div>
                  <div className="align-line medium"></div>
                </div>
              </button>
              <button 
                className={`align-btn ${element.style.textAlign === 'justify' ? 'active' : ''}`}
                onClick={() => handleStyleChange('textAlign', 'justify')}
              >
                <div className="align-icon align-justify">
                  <div className="align-line"></div>
                  <div className="align-line"></div>
                  <div className="align-line"></div>
                </div>
              </button>
            </div>
          </div>

          <div className="property-header">
            <h3>SPACING</h3>
            <button className="reset-btn" onClick={() => {
              handleResetStyle('letterSpacing');
              handleResetStyle('lineHeight');
            }}>Reset</button>
          </div>
          
          <div className="property-group">
            <div className="spacing-control">
              <label>LETTER SPACING</label>
              <div className="slider-container">
                <input 
                  type="range" 
                  min="-2" 
                  max="10" 
                  step="0.1"
                  value={parseFloat(element.style.letterSpacing) || 0} 
                  onChange={(e) => handleStyleChange('letterSpacing', `${e.target.value}px`)}
                  className="slider"
                />
                <div className="size-input-container">
                  <input 
                    type="text" 
                    value={`${parseInt((parseFloat(element.style.letterSpacing) || 0) * 100)}%`} 
                    readOnly
                    className="size-input"
                  />
                </div>
              </div>
            </div>
            
            <div className="spacing-control">
              <label>LINE HEIGHT</label>
              <div className="slider-container">
                <input 
                  type="range" 
                  min="0.5" 
                  max="3" 
                  step="0.01"
                  value={parseFloat(element.style.lineHeight) || 1.5} 
                  onChange={(e) => handleStyleChange('lineHeight', e.target.value)}
                  className="slider"
                />
                <div className="size-input-container">
                  <input 
                    type="text" 
                    value={parseFloat(element.style.lineHeight) || 1.5} 
                    readOnly
                    className="size-input"
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      
      {element.type === 'image' && (
        <>
          <div className="property-header">
            <h3>EDIT IMAGE</h3>
            <button className="reset-btn" onClick={() => handleResetStyle('all')}>Clear</button>
          </div>
          
          <div className="property-group">
            <div className="upload-container">
              <label htmlFor="image-upload" className="upload-btn">Upload Image</label>
              <input 
                id="image-upload" 
                type="file" 
                accept="image/*" 
                onChange={handleImageUpload}
                style={{ display: 'none' }} 
              />
              <p className="or-divider">OR</p>
              <input 
                type="text" 
                value={element.content} 
                onChange={handleContentChange}
                placeholder="Image URL"
                className="full-width-input"
              />
            </div>
            
            <div className="image-preview">
              {(element.isLoading || isUploading) && (
                <div className="upload-progress-container">
                  <div className="spinner"></div>
                  <div className="progress-text">
                    Uploading to Firebase Storage...
                    {uploadProgress > 0 && ` (${Math.round(uploadProgress)}%)`}
                  </div>
                  {uploadProgress > 0 && (
                    <div className="upload-progress">
                      <div 
                        className="progress-bar" 
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  )}
                </div>
              )}
              {element.content && !element.isLoading && !isUploading && (
                <img 
                  src={element.content} 
                  alt="Preview" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '150px',
                    objectFit: element.style.objectFit || 'contain',
                    borderRadius: element.style.borderRadius || '0px',
                    opacity: element.style.opacity || 1
                  }} 
                />
              )}
            </div>
          </div>
          
          <div className="property-header">
            <h3>IMAGE SETTINGS</h3>
            <button className="reset-btn" onClick={() => handleResetStyle('imageSettings')}>Reset</button>
          </div>
          
          <div className="property-group">
            <div className="property-row">
              <label>FIT</label>
              <select 
                value={element.style.objectFit || 'contain'} 
                onChange={(e) => handleStyleChange('objectFit', e.target.value)}
                className="style-select"
              >
                <option value="contain">Contain</option>
                <option value="cover">Cover</option>
                <option value="fill">Fill</option>
                <option value="none">None</option>
              </select>
            </div>
            
            <div className="property-row property-slider">
              <label>BORDER RADIUS</label>
              <div className="modern-slider-container">
                <input 
                  type="range" 
                  min="0" 
                  max="50" 
                  value={parseInt(element.style.borderRadius) || 0} 
                  onChange={(e) => {
                    const value = e.target.value;
                    e.target.style.setProperty('--value-percent', `${value * 2}%`);
                    handleStyleChange('borderRadius', `${value}px`);
                  }}
                  className="modern-slider"
                  style={{ '--value-percent': `${(parseInt(element.style.borderRadius) || 0) * 2}%` }}
                />
                <div className="slider-value">{parseInt(element.style.borderRadius) || 0}px</div>
              </div>
            </div>
            
            <div className="property-row property-slider">
              <label>OPACITY</label>
              <div className="modern-slider-container">
                <input 
                  type="range" 
                  min="0" 
                  max="100" 
                  value={Math.round((element.style.opacity || 1) * 100)} 
                  onChange={(e) => {
                    const value = e.target.value;
                    e.target.style.setProperty('--value-percent', `${value}%`);
                    handleStyleChange('opacity', value / 100);
                  }}
                  className="modern-slider"
                  style={{ '--value-percent': `${Math.round((element.style.opacity || 1) * 100)}%` }}
                />
                <div className="slider-value">{Math.round((element.style.opacity || 1) * 100)}%</div>
              </div>
            </div>
          </div>
        </>
      )}
      
      {element.type === 'video' && (
        <>
          <div className="property-header">
            <h3>EDIT VIDEO</h3>
            <button className="reset-btn" onClick={() => updateElement(element.id, { content: '' })}>Clear</button>
          </div>
          
          <div className="property-group">
            <div className="upload-container">
              <label htmlFor="video-upload" className="upload-btn">Upload Video</label>
              <input 
                id="video-upload" 
                type="file" 
                accept="video/*" 
                onChange={handleVideoUpload}
                style={{ display: 'none' }} 
              />
              <p className="or-divider">OR</p>
              <input 
                type="text" 
                value={element.content} 
                onChange={handleContentChange}
                placeholder="Video URL (YouTube or direct link)"
                className="full-width-input"
              />
            </div>
            
            <div className="video-preview">
              {(element.isLoading || isUploading) && (
                <div className="upload-progress-container">
                  <div className="spinner"></div>
                  <div className="progress-text">
                    Uploading to Firebase Storage...
                    {uploadProgress > 0 && ` (${Math.round(uploadProgress)}%)`}
                  </div>
                  {uploadProgress > 0 && (
                    <div className="upload-progress">
                      <div 
                        className="progress-bar" 
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  )}
                </div>
              )}
              
              {element.content && !isUploading && !element.isLoading && !element.content.includes('youtube') && !element.content.includes('youtu.be') && (
                <video src={element.content} controls style={{ maxWidth: '100%', maxHeight: '150px' }}></video>
              )}
              
              {element.content && !isUploading && !element.isLoading && (element.content.includes('youtube') || element.content.includes('youtu.be')) && (
                <div className="youtube-preview">
                  <div>YouTube video will appear on canvas</div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      
      {element.type === 'shape' && renderShapeProperties()}
      
      {element.type === 'rsvp-button' && renderRsvpButtonProperties()}
      
      {element.type === 'rsvp-form' && renderRsvpFormProperties()}

      {element.type === 'background' && renderBackgroundProperties()}

      {element.type === 'icon' && renderIconProperties()}
    </div>
  );
}

export default ElementProperties; 